/**
 * Mobile Services Page - Professional GTS Branding
 *
 * Agency marketing page with:
 * - White header with GTS logo and tagline
 * - Color-coded capability cards (Purple, Cyan, Gold)
 * - Professional contact form in card
 * - Floating chat FAB
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Code2,
  Cloud,
  BrainCircuit,
  MessageSquare,
  Check,
} from 'lucide-react';
import { MauticForm } from '../MauticForm';

interface CapabilityOption {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  colorTheme: 'purple' | 'cyan' | 'gold';
}

const CAPABILITIES: CapabilityOption[] = [
  {
    id: 'engineering',
    title: 'Custom Engineering',
    subtitle: 'Full-cycle development for Mobile, Web, and Desktop ecosystems.',
    icon: Code2,
    colorTheme: 'purple',
  },
  {
    id: 'cloud',
    title: 'Cloud & Infrastructure',
    subtitle: 'Scalable architecture, DevOps, and cloud migration strategies.',
    icon: Cloud,
    colorTheme: 'cyan',
  },
  {
    id: 'ai',
    title: 'AI & Data Strategy',
    subtitle: 'Machine learning integration, data pipelines, and automation.',
    icon: BrainCircuit,
    colorTheme: 'gold',
  },
];

const THEME_STYLES = {
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-500',
    iconBg: 'bg-purple-600',
    iconBgUnselected: 'bg-slate-100',
    checkBg: 'bg-purple-600',
  },
  cyan: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-500',
    iconBg: 'bg-cyan-600',
    iconBgUnselected: 'bg-slate-100',
    checkBg: 'bg-cyan-600',
  },
  gold: {
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    iconBg: 'bg-amber-500',
    iconBgUnselected: 'bg-slate-100',
    checkBg: 'bg-amber-500',
  },
};

interface MobileServicesPageProps {
  whatsappNumber?: string;
}

export const MobileServicesPage: React.FC<MobileServicesPageProps> = ({
  whatsappNumber = '+61400000000',
}) => {
  const [selectedInterest, setSelectedInterest] = useState('ai');

  const handleChat = () => {
    const selectedCapability = CAPABILITIES.find((c) => c.id === selectedInterest)?.title || 'your services';
    const text = encodeURIComponent(`Hi! I'm interested in ${selectedCapability}.`);
    window.open(`https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${text}`, '_blank');
  };

  // Capability Card Component
  const CapabilityCard = ({ capability }: { capability: CapabilityOption }) => {
    const isSelected = selectedInterest === capability.id;
    const Icon = capability.icon;
    const theme = THEME_STYLES[capability.colorTheme];

    return (
      <motion.button
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={() => setSelectedInterest(capability.id)}
        className={`w-full p-5 rounded-3xl border mb-4 flex items-start gap-4 text-left transition-all ${
          isSelected
            ? `${theme.bg} ${theme.border} border-2`
            : 'bg-white border-slate-100 shadow-sm hover:border-slate-200'
        }`}
      >
        <div
          className={`p-3 rounded-2xl transition-colors ${
            isSelected ? theme.iconBg : theme.iconBgUnselected
          }`}
        >
          <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
        </div>
        <div className="flex-1 pt-1">
          <p className={`font-bold text-lg ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>
            {capability.title}
          </p>
          <p className={`text-sm mt-1 leading-relaxed ${isSelected ? 'text-slate-600' : 'text-slate-400'}`}>
            {capability.subtitle}
          </p>
        </div>
        {isSelected && (
          <div className={`rounded-full p-1 mt-1 ${theme.checkBg}`}>
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </motion.button>
    );
  };

  return (
    <div className="flex flex-col min-h-full bg-slate-50 pb-24">
      {/* Header - White with GTS Brand */}
      <div className="bg-white px-6 pt-2 pb-6 border-b border-slate-100 rounded-b-[30px] shadow-sm z-10">
        {/* Logo Section */}
        <div className="flex items-center gap-3 mb-4">
          <img
            src="https://translate-gts.cchk.info/logo-light.png"
            alt="Ganda Tech Services"
            className="w-12 h-12 object-contain"
          />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              Ganda Tech Services
            </h1>
            <p className="text-slate-500 text-xs uppercase tracking-widest font-bold">
              Enterprise Engineering
            </p>
          </div>
        </div>

        <p className="text-slate-600 text-base leading-relaxed mt-1">
          We are a full-service consultancy specializing in scalable architecture, digital transformation, and intelligent systems.
        </p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto px-6 pt-6 pb-32">
        {/* Capabilities Section */}
        <h2 className="text-slate-900 text-lg font-bold mb-4 ml-1">Our Expertise</h2>

        {CAPABILITIES.map((capability) => (
          <CapabilityCard key={capability.id} capability={capability} />
        ))}

        {/* Contact Form */}
        <div className="mt-8 mb-4 flex items-center justify-between">
          <h2 className="text-slate-900 text-lg font-bold ml-1">Start a Conversation</h2>
          <span className="bg-slate-200 px-2 py-1 rounded-md text-slate-600 text-[10px] font-bold uppercase">
            Priority Support
          </span>
        </div>

        <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm">
          <MauticForm formId={8} className="mautic-gts-services" />
        </div>

        {/* Footer */}
        <div className="flex flex-col items-center mt-8 mb-4 gap-1">
          <img
            src="https://translate-gts.cchk.info/logo-light.png"
            alt="GTS"
            className="w-6 h-6 object-contain opacity-50"
          />
          <p className="text-slate-400 text-xs">
            Ganda Tech Services &bull; Sydney, Australia
          </p>
        </div>
      </div>

      {/* Floating Chat FAB */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={handleChat}
        className="fixed bottom-24 right-6 w-14 h-14 bg-slate-900 rounded-full flex items-center justify-center shadow-xl shadow-slate-400/40 z-50"
        aria-label="Start a conversation"
      >
        <MessageSquare className="w-6 h-6 text-white" />
      </motion.button>
    </div>
  );
};

export default MobileServicesPage;
